import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

interface Profile {
  id: string
  canonical_id: string
  tenant_id: string
  personality_signals: Record<string, number>
  preference_signals: Record<string, unknown>
  created_at: string
  updated_at: string
}

export default function Support() {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null)

  const { data: tenants } = useQuery<{ id: string; name: string; slug: string }[]>({
    queryKey: ['tenants'],
    queryFn: async () => {
      const res = await api.get('/tenants')
      return res.data
    },
  })

  const { data: profiles, isLoading: profilesLoading } = useQuery<Profile[]>({
    queryKey: ['profiles', selectedTenantId, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedTenantId) params.set('tenant_id', selectedTenantId)
      if (searchQuery) params.set('q', searchQuery)
      const res = await api.get(`/admin/profiles?${params}`)
      return res.data
    },
    enabled: !!selectedTenantId,
  })

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Support Tools</h2>

      {/* Profile Browser */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Profile Browser</h3>
          <p className="text-sm text-gray-500 mt-1">Browse and debug profiles across tenants</p>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="label">Tenant</label>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="input"
              >
                <option value="">Select a tenant...</option>
                {tenants?.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.slug})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by canonical ID..."
                className="input"
              />
            </div>
          </div>

          {/* Results */}
          {!selectedTenantId ? (
            <div className="text-center py-12 text-gray-500">
              Select a tenant to browse profiles
            </div>
          ) : profilesLoading ? (
            <div className="text-center py-12 text-gray-500">Loading profiles...</div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Profile List */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    Profiles ({profiles?.length || 0})
                  </span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {profiles?.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No profiles found</div>
                  ) : (
                    profiles?.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => setSelectedProfile(profile)}
                        className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 ${
                          selectedProfile?.id === profile.id ? 'bg-personic-50' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900 truncate">
                          {profile.canonical_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          Updated: {new Date(profile.updated_at).toLocaleString()}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Profile Detail */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Profile Detail</span>
                </div>
                {selectedProfile ? (
                  <div className="p-4 max-h-96 overflow-y-auto">
                    <div className="space-y-4">
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          Canonical ID
                        </span>
                        <p className="text-gray-900 font-mono text-sm">
                          {selectedProfile.canonical_id}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          Profile ID
                        </span>
                        <p className="text-gray-900 font-mono text-sm">{selectedProfile.id}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          Personality Signals
                        </span>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                          {JSON.stringify(selectedProfile.personality_signals, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          Preference Signals
                        </span>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                          {JSON.stringify(selectedProfile.preference_signals, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Select a profile to view details
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schema Validation */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Schema Validation</h3>
        <p className="text-gray-600 mb-4">
          Validate profile data against the Personic v3.2 schema specification.
        </p>
        <button className="btn btn-secondary">Run Schema Validation</button>
      </div>

      {/* Debug Tools */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Debug Tools</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button className="btn btn-secondary">Test Distillation</button>
          <button className="btn btn-secondary">Test Persona Generation</button>
          <button className="btn btn-secondary">View System Logs</button>
        </div>
      </div>
    </div>
  )
}
